import React, { useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import background from "../../assets/background.png";
import logoImg from "../../assets/logo_text.png";

import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import { HiOutlineLockClosed } from "react-icons/hi";
import { FaCheck, FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import UserService from "../../services/user.service";
import { PASSWORD_RULES } from "../../components/Settings/UserModal/RegisterStep";

export default function ConfirmNewUser() {
  const { params } = useRouteMatch();
  const { token } = params as { token: string };
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });

  const passwordRulesMet = PASSWORD_RULES.map((rule) =>
    rule.test(form.password),
  );
  const allRulesMet = passwordRulesMet.every(Boolean);
  const passwordsMatch = form.password === form.confirmPassword;
  const confirmFilled = form.confirmPassword.length > 0;

  function submitHandler() {
    if (!form.password) return toast.error("Insira uma senha válida");
    if (!allRulesMet)
      return toast.error("A senha não atende os critérios de segurança");
    if (!form.confirmPassword)
      return toast.error("Insira uma confirmação de senha válida");

    if (!passwordsMatch)
      return toast.error("As senhas não coincidem");

    if (token) {
      setLoading(true);
      UserService.confirmUser(token, form.password)
        .then(() => {
          toast.success("Senha atualizada com sucesso!");
          history.push("/login");
        })
        .catch((error) => {
          toast.error("Erro ao atualizar senha");
        })
        .finally(() => setLoading(false));
    }
  }

  return (
    <Box
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      width={"100vw"}
      height={"100vh"}
      sx={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: 3,
          pt: 4,
          width: 408,
          borderRadius: "16px",
        }}
      >
        <Box display={"flex"} justifyContent={"center"}>
          <img src={logoImg} alt="" />
        </Box>

        <Typography variant={"h4"}>Criar uma senha</Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box ml={"5px"} mr={"12px"}>
            <HiOutlineLockClosed size={"24px"} color="#A0A3A6" />
          </Box>

          <TextField
            label={"Senha"}
            size="small"
            onChange={(e: any) =>
              setForm({ ...form, password: e.target.value })
            }
            value={form.password}
            variant="outlined"
            fullWidth
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {form.password.length > 0 && (
          <Box
            p={1.5}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#fafafa",
            }}
          >
            {PASSWORD_RULES.map((rule, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
                {passwordRulesMet[i] ? (
                  <FaCheck color="#4caf50" size={12} />
                ) : (
                  <FaTimes color="#f44336" size={12} />
                )}
                <Typography
                  variant="caption"
                  color={passwordRulesMet[i] ? "#4caf50" : "#f44336"}
                >
                  {rule.label}
                </Typography>
              </Box>
            ))}
            {confirmFilled && (
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                {passwordsMatch ? (
                  <FaCheck color="#4caf50" size={12} />
                ) : (
                  <FaTimes color="#f44336" size={12} />
                )}
                <Typography
                  variant="caption"
                  color={passwordsMatch ? "#4caf50" : "#f44336"}
                >
                  As senhas precisam ser iguais
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box ml={"5px"} mr={"12px"}>
            <HiOutlineLockClosed size={"24px"} color="#A0A3A6" />
          </Box>

          <TextField
            label={"Confirmar senha"}
            size="small"
            onChange={(e: any) =>
              setForm({ ...form, confirmPassword: e.target.value })
            }
            value={form.confirmPassword}
            variant="outlined"
            fullWidth
            type={showConfirm ? "text" : "password"}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowConfirm((v) => !v)}
                    edge="end"
                  >
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display={"flex"} flexDirection={"column"} gap={2}>
          <Button
            variant={"contained"}
            color={"primary"}
            onClick={submitHandler}
            disabled={!allRulesMet || !passwordsMatch || !confirmFilled}
          >
            {!loading ? "Redefinir senha" : <CircularProgress size={24} />}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
